import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Grid
} from '@material-ui/core';
import { companyTypes } from './companyTypes';
import useVrackApi from '../../hooks/useVrackApi';
import { setOpenSnackbar } from '../../store/slices/snackbarSlice';

AddCompanyDialog.propTypes = {
  isOpen: PropTypes.bool,
  closeHandler: PropTypes.func,
  // No-arg callback invoked after a successful add — typically the parent's
  // `refetch` for the companies list. The dialog owns its own API call now
  // (POST /admin/companies) and uses this to tell the parent to reload.
  onAdded: PropTypes.func
};

export default function AddCompanyDialog({ isOpen, closeHandler, onAdded }) {
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [type, setType] = useState(companyTypes[0]);
  const [contactEmail, setContactEmail] = useState('');
  const [nameError, setNameError] = useState('');
  const [idError, setIdError] = useState('');
  const [contactEmailError, setContactEmailError] = useState('');
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));
  const { fetchData: addCompanyApi } = useVrackApi('post', '/admin/companies');

  const validateName = (value) => {
    if (!value.trim()) {
      setNameError('Enter a company name.');
      return false;
    }
    setNameError('');
    return true;
  };

  const validateId = (value) => {
    if (!value.trim()) {
      setIdError('Enter a unique company ID.');
      return false;
    }
    setIdError('');
    return true;
  };

  const validateContactEmail = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setContactEmailError('Enter a contact email.');
      return false;
    }
    // Lightweight shape check — matches the field's type="email" semantics
    // without pulling in a full RFC 5322 parser. Backend is the source of truth.
    const emailShape = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailShape.test(trimmed)) {
      setContactEmailError('Enter a valid email address.');
      return false;
    }
    setContactEmailError('');
    return true;
  };

  const handleAdd = async () => {
    const nameValid = validateName(name);
    const idValid = validateId(id);
    const contactEmailValid = validateContactEmail(contactEmail);
    if (!nameValid || !idValid || !contactEmailValid) return;

    setLoading(true);
    try {
      await addCompanyApi({
        data: {
          company_id: id.trim(),
          name: name.trim(),
          type,
          contact_email: contactEmail.trim()
        }
      });
      openSnackbar({ message: `${name.trim()} added successfully.` });
      if (onAdded) onAdded();
      handleReset();
    } catch (error) {
      const message =
        error?.response?.status === 409
          ? 'A company with that ID already exists.'
          : 'Failed to add company.';
      openSnackbar({ message, severity: 'error' });
      setLoading(false);
    }
  };

  const handleReset = () => {
    setName('');
    setId('');
    setType(companyTypes[0]);
    setContactEmail('');
    setNameError('');
    setIdError('');
    setContactEmailError('');
    setLoading(false);
    closeHandler();
  };

  const handleCancel = () => {
    if (loading) return;
    handleReset();
  };

  return (
    <Dialog open={isOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Company</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Create a company record that can own teams and receive billing reports.
        </DialogContentText>
        <Grid container item spacing={1} mt={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              label="Company ID"
              fullWidth
              variant="outlined"
              value={id}
              onChange={(e) => {
                setId(e.target.value);
                validateId(e.target.value);
              }}
              error={!!idError}
              helperText={idError || ' '}
              placeholder="cmp-004"
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              autoFocus
              required
              label="Company name"
              fullWidth
              variant="outlined"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                validateName(e.target.value);
              }}
              error={!!nameError}
              helperText={nameError || ' '}
              placeholder="Example: United Airlines"
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Company type"
              fullWidth
              variant="outlined"
              value={type}
              onChange={(e) => setType(e.target.value)}
              helperText=" "
              disabled={loading}
            >
              {companyTypes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              type="email"
              label="Company contact email"
              fullWidth
              variant="outlined"
              value={contactEmail}
              onChange={(e) => {
                setContactEmail(e.target.value);
                validateContactEmail(e.target.value);
              }}
              error={!!contactEmailError}
              helperText={contactEmailError || ' '}
              placeholder="admin@panasonic.aero"
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleAdd} disabled={loading}>
          Add Company
        </Button>
      </DialogActions>
    </Dialog>
  );
}
